import { FC, useEffect, useMemo, useState } from 'react'
import { Flex, Button } from 'antd'
import { useTranslation } from 'react-i18next'
import Cookies from 'universal-cookie'
import { authStore } from '../store/auth'
import { API_BASE_URL } from '../config'

interface AuthWarningProps {
	onAuthRedirect?: () => void
}

export const AuthWarning: FC<AuthWarningProps> = ({
	onAuthRedirect
}) => {
	const { t } = useTranslation()
	const cookie = useMemo(() => new Cookies(), [])
	const [isRefreshing, setIsRefreshing] = useState(false)

	// Try to refresh tokens on component mount if refresh token exists
	useEffect(() => {
		const tryRefresh = async () => {
			const accessToken = cookie.get('access')
			const refreshToken = cookie.get('refresh')

			if (!accessToken && refreshToken && !isRefreshing) {
				setIsRefreshing(true)
				try {
					console.log(
						'🔄 AuthWarning: Attempting token refresh...'
					)
					const { refresh } = authStore.getState()
					await refresh()
					console.log(
						'✅ AuthWarning: Tokens refreshed successfully'
					)
					// Refresh updated the store (isLoggedIn: true) — parent re-renders
				} catch (error) {
					console.error(
						'❌ AuthWarning: Token refresh failed:',
						error
					)
				} finally {
					setIsRefreshing(false)
				}
			}
		}
		tryRefresh()
	}, [cookie, isRefreshing])

	const handleAuthRedirect = () => {
		if (onAuthRedirect) {
			onAuthRedirect()
		} else {
			// Default redirect to smartdepo.uz login
			window.open(`${API_BASE_URL}/api/v1/auth/login`, '_blank')
		}
	}

	const handleRefresh = async () => {
		const refreshToken = cookie.get('refresh')
		if (refreshToken) {
			setIsRefreshing(true)
			try {
				console.log('🔄 Manual refresh attempt...')
				const { refresh } = authStore.getState()
				await refresh()
				console.log('✅ Manual refresh successful')
				// Refresh updated the store (isLoggedIn: true) — parent re-renders
			} catch (error) {
				console.error('❌ Manual refresh failed:', error)
				handleAuthRedirect()
			} finally {
				setIsRefreshing(false)
			}
		} else {
			window.location.reload()
		}
	}

	return (
		<Flex
			vertical
			align="center"
			justify="center"
			style={{
				position: 'fixed',
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				backgroundColor: 'rgba(0, 0, 0, 0.8)',
				backdropFilter: 'blur(10px)',
				zIndex: 9999,
				color: 'white'
			}}>
			<Flex
				vertical
				align="center"
				gap={24}
				style={{
					backgroundColor: 'rgba(255, 255, 255, 0.95)',
					padding: '48px',
					borderRadius: '16px',
					color: '#333',
					minWidth: '400px',
					maxWidth: '500px',
					textAlign: 'center',
					boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
					border: '1px solid rgba(255, 255, 255, 0.2)'
				}}>
				<div
					style={{
						width: '64px',
						height: '64px',
						borderRadius: '50%',
						backgroundColor: '#FF6B6B',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						fontSize: '24px',
						color: 'white',
						animation: 'pulse 2s infinite'
					}}>
					🔒
				</div>

				<h2
					style={{
						margin: 0,
						color: '#333',
						fontSize: '28px',
						fontWeight: 'bold'
					}}>
					{isRefreshing
						? t('Refreshing Tokens...')
						: t('Authentication Required')}
				</h2>

				<p
					style={{
						margin: 0,
						color: '#666',
						fontSize: '16px',
						lineHeight: '1.6',
						maxWidth: '400px'
					}}>
					{isRefreshing
						? t(
								'Please wait while we refresh your authentication tokens...'
							)
						: t(
								'To access this content, you need to authenticate first. Please login through SmartDepo platform to acquire the necessary tokens.'
							)}
				</p>

				<div
					style={{
						backgroundColor: '#f8f9fa',
						padding: '16px',
						borderRadius: '8px',
						border: '1px solid #e9ecef',
						width: '100%',
						textAlign: 'left'
					}}>
					<strong
						style={{
							color: '#495057',
							fontSize: '14px'
						}}>
						{t('Authentication URL')}:
					</strong>
					<div
						style={{
							fontFamily: 'Monaco, Consolas, monospace',
							fontSize: '12px',
							color: '#007bff',
							marginTop: '4px',
							wordBreak: 'break-all'
						}}>
						{`${API_BASE_URL}/api/v1/auth/login`}
					</div>
				</div>

				<Flex gap={16} style={{ width: '100%' }}>
					<Button
						size="large"
						onClick={handleRefresh}
						disabled={isRefreshing}
						loading={isRefreshing}
						style={{
							flex: 1,
							height: '48px',
							borderRadius: '8px',
							border: '2px solid #6c757d',
							color: '#6c757d',
							fontWeight: '500'
						}}>
						{cookie.get('refresh')
							? t('Try Refresh Token')
							: t('Refresh Page')}
					</Button>

					<Button
						type="primary"
						size="large"
						onClick={handleAuthRedirect}
						disabled={isRefreshing}
						style={{
							flex: 1,
							height: '48px',
							borderRadius: '8px',
							backgroundColor: '#007bff',
							borderColor: '#007bff',
							fontWeight: '500',
							fontSize: '16px'
						}}>
						{t('Login Now')}
					</Button>
				</Flex>

				<p
					style={{
						margin: 0,
						fontSize: '12px',
						color: '#999',
						maxWidth: '350px'
					}}>
					{t(
						'After successful authentication, your tokens will be automatically stored and you can access the content.'
					)}
				</p>
			</Flex>
		</Flex>
	)
}
